<template>
    <div id="device-list-filters">
        <form class="mt-1">
            <output
                class="visually-hidden d-xl-none"
            >
                {{ resultsAccessibleLabel }}
            </output>

            <accordion
                v-if="hasCategories"
                :class="searchMode ? 'mt-1' : 'mt-2'"
                element-type="h3"
                :cards="categoryParentCard"
                :accordion-id="searchMode ? 'device-list-filters-search-categories' : 'device-list-filters-categories-parent'"
                is-large
                @on-toggle="trackTms"
            >
                <template v-if="!searchMode && hasNoSubCategories" #filter-category-parent-header>
                    <fieldset>
                        <legend :aria-labelledby="'filter-category-parent-header'" />
                        <ul class="list-unstyled ms-1">
                            <li>
                                <p
                                    v-if="selectedCategory === null"
                                    class="fw-bold mb-0"
                                >
                                    <span class="icon icon-checkbox-tick" aria-hidden="true" /><span class="ms-1" v-html="allProductsLabel" />
                                </p>
                                <Lien
                                    v-else
                                    :label="allProductsLabel"
                                    prevent-default
                                    is-button
                                    :tms-category="tmsCategory(departmentLabel)"
                                    :tms-action="tmsAction('tous')"
                                    :tms-label="tmsLabel(allProductsLabel)"
                                    @click="selectCategory(null)"
                                />
                            </li>
                            <li
                                v-for="category in categories"
                                :key="category.seoId"
                            >
                                <p
                                    v-if="selectedCategory === category.seoId"
                                    class="fw-bold mb-0"
                                >
                                    <span class="icon icon-checkbox-tick" aria-hidden="true" /><span class="ms-1" v-html="category.label" />
                                </p>
                                <Lien
                                    v-else
                                    :label="category.label"
                                    prevent-default
                                    is-button
                                    :tms-category="tmsCategory(departmentLabel)"
                                    :tms-action="tmsAction(category.label)"
                                    :tms-label="tmsLabel(category.label)"
                                    @click="selectCategory(category.seoId)"
                                />
                            </li>
                        </ul>
                    </fieldset>
                </template>
                <template v-else-if="!searchMode && categoryCards.length > 0" #filter-category-parent-header>
                    <fieldset>
                        <legend aria-labelledby="filter-category-parent-header" />
                        <accordion
                            element-type="h4"
                            :cards="categoryCards"
                            one-at-time
                            accordion-id="device-list-filters-categories"
                            @on-toggle="trackTms"
                        >
                            <template
                                v-for="category in categories"
                                #[`filter-category-header-${category.seoId}`]
                            >
                                <fieldset :key="category.seoId">
                                    <legend :aria-labelledby="'filter-category-header-' + category.seoId" />
                                    <ul class="list-unstyled ms-1">
                                        <li>
                                            <p
                                                v-if="selectedCategory === category.seoId && selectedSubCategory === null"
                                                class="fw-bold mb-0"
                                            >
                                                <span class="icon icon-checkbox-tick" aria-hidden="true" /><span class="ms-1" v-html="allProductsLabel" />
                                            </p>
                                            <Lien
                                                v-else
                                                :label="allProductsLabel"
                                                prevent-default
                                                is-button
                                                :tms-category="tmsCategory(departmentLabel)"
                                                :tms-action="tmsAction(category.label)"
                                                :tms-label="tmsLabel(allProductsLabel)"
                                                @click="selectCategory(category.seoId)"
                                            />
                                        </li>
                                        <li
                                            v-for="subCategory in category.subCategories"
                                            :key="subCategory.seoId"
                                        >
                                            <p
                                                v-if="selectedSubCategory === subCategory.seoId"
                                                class="fw-bold mb-0"
                                            >
                                                <span class="icon icon-checkbox-tick" aria-hidden="true" /><span class="ms-1" v-html="subCategory.label" />
                                            </p>
                                            <Lien
                                                v-else
                                                :label="subCategory.label"
                                                prevent-default
                                                is-button
                                                :tms-category="tmsCategory(departmentLabel)"
                                                :tms-action="tmsAction(category.label)"
                                                :tms-label="tmsLabel(subCategory.label)"
                                                @click="selectCategory(category.seoId, subCategory.seoId)"
                                            />
                                        </li>
                                    </ul>
                                </fieldset>
                            </template>
                        </accordion>
                    </fieldset>
                </template>
                <template v-if="searchMode" #filter-search-category-header>
                    <fieldset>
                        <legend aria-labelledby="filter-search-category-header" />
                        <ul class="list-unstyled ms-1">
                            <li>
                                <p v-if="selectedSearchCategory === null" class="fw-bold mb-0" aria-current="true">
                                    <span class="icon icon-checkbox-tick" aria-hidden="true" />
                                    <span class="ms-1">{{ allProductsLabel }}</span>
                                </p>
                                <Lien
                                    v-else
                                    :label="allProductsLabel"
                                    prevent-default
                                    is-button
                                    @click="selectSearchCategory(null)"
                                />
                            </li>
                            <li v-for="category in searchCategories" :key="category.value">
                                <p v-if="selectedSearchCategory === category.value" class="fw-bold mb-0" aria-current="true">
                                    <span class="icon icon-checkbox-tick" aria-hidden="true" />
                                    <span class="ms-1">{{ category.label }}</span>
                                </p>
                                <Lien
                                    v-else
                                    :label="category.label"
                                    prevent-default
                                    is-button
                                    @click="selectSearchCategory(category.value)"
                                />
                            </li>
                        </ul>
                    </fieldset>
                </template>
            </accordion>

            <fieldset v-if="thirdPartyEnabled" class="mt-1">
                <legend aria-labelledby="firstPartyOnlyLabel" />
                <div class="d-flex">
                    <label id="firstPartyOnlyLabel" for="firstPartyOnly" class="standard">
                        <span>Vendu uniquement par&nbsp;<strong>{{ shopBrandLabel }}</strong></span>
                    </label>
                    <SwitchButton
                        id="firstPartyOnly"
                        class="ms-auto"
                        :checked="firstPartyOnlyChecked"
                        @change="toggleFirstPartyOnly"
                    />
                </div>
            </fieldset>

            <accordion
                class="mt-1"
                element-type="h3"
                :cards="accordionCards"
                accordion-id="device-list-filters-accordion"
                is-large
                @on-toggle="trackTms"
            >
                <template v-for="selectorGroup in selectorGroupsCheckboxes" #[`filter-selector-group-header-${selectorGroup.id}`]>
                    <fieldset :key="selectorGroup.seoId">
                        <legend aria-labelledby="'filter-selector-group-header-' + selectorGroup.id" />
                        <ul class="list-unstyled" aria-labelledby="filter-brand-header">
                            <li
                                v-for="selector in selectorGroup.selectors"
                                :key="selector.id"
                            >
                                <CheckBox
                                    :id="selector.id"
                                    :label="selector.label"
                                    :aria-label="selector.label"
                                    :model-value="selector.isChecked"
                                    @change="toggleSelector(selector.id, $event)"
                                />
                            </li>
                        </ul>
                    </fieldset>
                </template>

                <template v-if="statusCheckboxes.length > 0" #filter-status-header>
                    <fieldset>
                        <legend aria-labelledby="filter-status-header" />
                        <ul class="list-unstyled" aria-labelledby="filter-status-header">
                            <li
                                v-for="checkbox in statusCheckboxes"
                                :key="checkbox.name"
                            >
                                <CheckBox
                                    :id="checkbox.id"
                                    :label="checkbox.label"
                                    :aria-label="checkbox.accessibleLabel"
                                    :model-value="checkbox.isChecked"
                                    :is-disabled="checkbox.disabled"
                                    @change="toggleStatus(checkbox.id, $event)"
                                />
                            </li>
                        </ul>
                    </fieldset>
                </template>

                <template v-if="brandCheckboxes.length > 0" #filter-brand-header>
                    <fieldset>
                        <legend aria-labelledby="filter-brand-header" />
                        <ob1-search-field
                            id="brand-search-field"
                            :input-value="brandSearchValue"
                            :label="brandSearchLabel"
                            :with-auto-completion="false"
                            :show-auto-completion="false"
                            is-mini
                            @input="filterBrands"
                        />
                        <output
                            class="visually-hidden"
                        >
                            {{ availableBrandFoundLabel }}
                        </output>
                        <ul class="list-unstyled" aria-labelledby="filter-brand-header">
                            <li
                                v-for="checkbox in notCollapsableBrandCheckboxes"
                                :key="checkbox.name"
                            >
                                <CheckBox
                                    :id="checkbox.id"
                                    :label="checkbox.label"
                                    :aria-label="checkbox.accessibleLabel"
                                    :model-value="checkbox.isChecked"
                                    @change="toggleBrand(checkbox.id, $event)"
                                />
                            </li>
                            <LinkFoldUnFold
                                v-if="collapsableBrandCheckboxes.length > 0"
                                button-id="collapsed-brand-header"
                                :button-label="collapsableBrandButtonLabel"
                                :with-link-bottom="true"
                                collapsed-block-id="brand-collapsed-block"
                                @ob1-liens-haut-bas-show-block="brandShow"
                                @ob1-liens-haut-bas-hide-block="brandHide"
                            >
                                <li
                                    v-for="checkbox in collapsableBrandCheckboxes"
                                    :key="checkbox.name"
                                >
                                    <CheckBox
                                        :id="checkbox.id"
                                        :label="checkbox.label"
                                        :aria-label="checkbox.accessibleLabel"
                                        :model-value="checkbox.isChecked"
                                        @change="toggleBrand(checkbox.id, $event)"
                                    />
                                </li>
                            </LinkFoldUnFold>
                        </ul>
                    </fieldset>
                </template>

                <template v-if="bonusRangesCheckboxes.length > 0" #filter-bonus-header>
                    <fieldset>
                        <legend aria-labelledby="filter-bonus-header" />
                        <ul class="list-unstyled" aria-labelledby="filter-bonus-header">
                            <li
                                v-for="checkbox in bonusRangesCheckboxes"
                                :key="checkbox.name"
                            >
                                <CheckBox
                                    :id="checkbox.id"
                                    :label="checkbox.label"
                                    :aria-label="checkbox.accessibleLabel"
                                    :model-value="checkbox.isChecked"
                                    :is-disabled="checkbox.disabled"
                                    @change="toggleBonusRanges(checkbox.id, $event)"
                                />
                            </li>
                        </ul>
                    </fieldset>
                </template>

                <template v-if="priceCheckboxes.length > 0" #filter-price-header>
                    <fieldset>
                        <legend aria-labelledby="filter-price-header" />
                        <ul class="list-unstyled" aria-labelledby="filter-price-header">
                            <li
                                v-for="checkbox in priceCheckboxes"
                                :key="checkbox.name"
                            >
                                <CheckBox
                                    :id="checkbox.id"
                                    :label="checkbox.label"
                                    :aria-label="checkbox.accessibleLabel"
                                    :model-value="checkbox.isChecked"
                                    :is-disabled="checkbox.disabled"
                                    @change="togglePrice(checkbox.id, $event)"
                                />
                            </li>
                        </ul>
                    </fieldset>
                </template>

                <template v-if="compatibilityCheckboxes.length > 0" #filter-compatibility-header>
                    <fieldset>
                        <legend aria-labelledby="filter-compatibility-header" />
                        <ul class="list-unstyled" aria-labelledby="filter-compatibility-header">
                            <li
                                v-for="checkbox in compatibilityCheckboxes"
                                :key="checkbox.value"
                            >
                                <CheckBox
                                    :id="checkbox.id"
                                    :label="checkbox.label"
                                    :aria-label="checkbox.accessibleLabel"
                                    :model-value="checkbox.isChecked"
                                    :is-disabled="checkbox.disabled"
                                    @change="toggleCompatibility(checkbox.id, $event)"
                                />
                            </li>
                        </ul>
                    </fieldset>
                </template>
                <template v-if="noteCheckboxes.length > 0" #filter-note-header>
                    <fieldset>
                        <legend aria-labelledby="filter-note-header" />
                        <ul class="list-unstyled" aria-labelledby="filter-note-header">
                            <li
                                v-for="checkbox in noteCheckboxes"
                                :key="checkbox.name"
                                class="d-flex mb-1"
                            >
                                <CheckBox
                                    :id="checkbox.id"
                                    class="mb-0"
                                    :aria-label="checkbox.accessibleLabel"
                                    :model-value="checkbox.isChecked"
                                    :is-disabled="checkbox.disabled"
                                    @change="toggleNote(checkbox.id, $event)"
                                />
                                <div aria-hidden="true" class="d-flex">
                                    <Rating
                                        class="filter-rating-item"
                                        :average="Number(checkbox.value)"
                                        :rating-results-height="1.010"
                                        :font-size="0.875"
                                        :text="ratingText(checkbox)"
                                    />
                                </div>
                            </li>
                        </ul>
                    </fieldset>
                </template>
                <template v-if="efficiencyClassesCheckboxes.length > 0" #filter-efficiency-classes-header>
                    <fieldset>
                        <legend aria-labelledby="filter-efficiency-classes-header" />
                        <ul class="list-unstyled" aria-labelledby="filter-efficiency-classes-header">
                            <li
                                v-for="checkbox in notCollapsableEfficiencyClassesCheckboxes"
                                :key="checkbox.value"
                                class="d-flex"
                            >
                                <CheckBox
                                    :id="checkbox.id"
                                    :aria-label="checkbox.accessibleLabel"
                                    :model-value="checkbox.isChecked"
                                    :is-disabled="checkbox.disabled"
                                    @change="toggleEfficiencyClass(checkbox.id, $event)"
                                />
                                <div aria-hidden="true" class="d-flex">
                                    <img
                                        :alt="'terminal de classe énergétique ' + checkbox.label"
                                        height="19"
                                        :src="checkbox.efficiencyClassImage"
                                    >
                                </div>
                            </li>
                            <LinkFoldUnFold
                                v-if="collapsableEfficiencyClassesCheckboxes.length > 0"
                                button-id="collapsed-efficiency-classes-header"
                                :button-label="collapsableEfficiencyClassesButtonLabel"
                                :with-link-bottom="true"
                                collapsed-block-id="efficiency-classes-collapsed-block"
                                @ob1-liens-haut-bas-show-block="efficiencyClassesShow"
                                @ob1-liens-haut-bas-hide-block="efficiencyClassesHide"
                            >
                                <li
                                    v-for="checkbox in collapsableEfficiencyClassesCheckboxes"
                                    :key="checkbox.name"
                                    class="d-flex"
                                >
                                    <CheckBox
                                        :id="checkbox.id"
                                        :aria-label="checkbox.accessibleLabel"
                                        :model-value="checkbox.isChecked"
                                        :is-disabled="checkbox.disabled"
                                        @change="toggleEfficiencyClass(checkbox.id, $event)"
                                    />
                                    <div aria-hidden="true" class="d-flex">
                                        <img
                                            :alt="'terminal de classe énergétique ' + checkbox.label"
                                            height="19"
                                            :src="checkbox.efficiencyClassImage"
                                        >
                                    </div>
                                </li>
                            </LinkFoldUnFold>
                        </ul>
                    </fieldset>
                </template>
            </accordion>
        </form>
    </div>
</template>

<script>
import Accordion from "../../../common/components/ob1/Accordion.vue";
import SwitchButton from "../../../common/components/ob1/SwitchButton.vue";
import CheckBox from "../../../common/components/ob1/CheckBox.vue";
import Lien from "../../../common/components/ob1/Lien.vue";
import LinkFoldUnFold from "../../../common/components/ob1/LinkFoldUnfold.vue";
import Rating from "../../../common/components/ob1/Rating.vue";
import {toTmsFormat} from "../../../common/utils/TmsUtils";

export default {
    name: "DeviceListFilters",
    components: {
        Rating,
        Accordion,
        Lien,
        LinkFoldUnFold,
        CheckBox,
        SwitchButton
    },
    props: {
        searchMode: {
            type: Boolean,
            default: false
        },
        firstPartyOnlyChecked: {
            type: Boolean,
            default: false
        },
        brandCheckboxes: {
            type: Array,
            default: () => []
        },
        searchCategories: {
            type: Array,
            default: () => []
        },
        priceCheckboxes: {
            type: Array,
            default: () => []
        },
        noteCheckboxes: {
            type: Array,
            default: () => []
        },
        categories: {
            type: Array,
            default: () => []
        },
        selectedCategory: {
            type: String,
            default: null
        },
        selectedSearchCategory: {
            type: String,
            default: null
        },
        selectedSubCategory: {
            type: String,
            default: null
        },
        brandSearchLabel: {
            type: String,
            required: true
        },
        brandSearchValue: {
            type: String,
            required: true
        },
        collapsedBrandsLabel: {
            type: String,
            required: true
        },
        notCollapsedBrandsLabel: {
            type: String,
            required: true
        },
        resultsAccessibleLabel: {
            type: String,
            default: undefined
        },
        thirdPartyEnabled: {
            type: Boolean,
            default: false
        },
        departmentLabel: {
            type: String,
            default: ''
        },
        selectorGroupsCheckboxes: {
            type: Array,
            default: () => []
        },
        shopBrand: {
            type: String,
            required: true
        },
        statusCheckboxes: {
            type: Array,
            default: () => []
        },
        compatibilityCheckboxes: {
            type: Array,
            default: () => []
        },
        bonusRangesCheckboxes: {
            type: Array,
            default: () => []
        },
        efficiencyClassesCheckboxes: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            notCollapsableBrandNumber: 5,
            brandsCollapse: false,
            allProductsLabel: "Voir tous les produits",
            efficiencyClassesCollapse: false,
        };
    },
    computed: {
        hasCategories() {
            return this.searchMode
                ? this.searchCategories.length > 0 || this.selectedSearchCategory !== null
                : this.categoryCards.length > 0;
        },
        categoryParentCard(){
            let cards = [];
            cards.push(
                {
                    headingId: this.searchMode ? "filter-search-category-header" : "filter-category-parent-header",
                    collapseId: this.searchMode ? "filter-search-category-block" : "filter-category-parent-block",
                    icon: null,
                    title: "Catégories",
                    expanded: true,
                    tms : {
                        trackZone: this.searchMode ? "filtres_commun" : "header_" + toTmsFormat(this.departmentLabel),
                        trackLabel: "filtre_categories",
                        trackAction: "filtre_categories"
                    }
                }
            )
            return cards;
        },
        categoryCards(){
            let cards = [];
            this.categories.forEach((category) => {
                cards.push(
                    {
                        headingId: "filter-category-header-" + category.seoId,
                        collapseId: "filter-category-block-" + category.seoId,
                        icon: null,
                        title: category.label,
                        expanded: this.selectedCategory === category.seoId,
                        tms : {
                            trackZone: `header_${toTmsFormat(this.departmentLabel)}`,
                            trackLabel: `categorie_${toTmsFormat(category.label)}`,
                            trackAction: `categorie_${toTmsFormat(category.label)}`
                        }
                    }
                )
            });

            return cards;
        },
        accordionCards() {
            let cards = [];
            if (this.selectorGroupsCheckboxes.length > 0) {
                this.selectorGroupsCheckboxes.forEach((selectorGroup) => {
                    cards.push(
                        {
                            headingId: "filter-selector-group-header-" + selectorGroup.id,
                            collapseId: "filter-selector-group-block-" + selectorGroup.id,
                            icon: null,
                            title: selectorGroup.label,
                            expanded: true,
                            tms : {
                                trackZone: "filtres_commun",
                                trackLabel: `filtre_${toTmsFormat(selectorGroup.label)}`,
                                trackAction: `filtre_${toTmsFormat(selectorGroup.label)}`
                            }
                        }
                    )
                });
            }
            if (this.statusCheckboxes.length > 0) {
                cards.push(
                    {
                        headingId: "filter-status-header",
                        collapseId: "filter-status-block",
                        icon: null,
                        title: "État",
                        expanded: true,
                        tms : {
                            trackZone: "filtres_commun",
                            trackLabel: "filtre_status",
                            trackAction: "filtre_status"
                        }
                    }
                )
            }
            if (this.brandCheckboxes.length > 0) {
                cards.push(
                    {
                        headingId: "filter-brand-header",
                        collapseId: "filter-brand-block",
                        icon: null,
                        title: "Marques",
                        expanded: true,
                        tms : {
                            trackZone: "filtres_commun",
                            trackLabel: "filtre_marques",
                            trackAction: "filtre_marques"
                        }
                    }
                )
            }

            if (this.bonusRangesCheckboxes.length > 0) {
                cards.push(
                    {
                        headingId: "filter-bonus-header",
                        collapseId: "filter-bonus-block",
                        icon: null,
                        title: "Bonus reprise",
                        expanded: true,
                        tms : {
                            trackZone: "filtres_commun",
                            trackLabel: "filtre_bonus",
                            trackAction: "filtre_bonus"
                        }
                    }
                )
            }

            if (this.priceCheckboxes.length > 0) {
                cards.push(
                    {
                        headingId: "filter-price-header",
                        collapseId: "filter-price-block",
                        icon: null,
                        title: "Prix",
                        expanded: true,
                        tms : {
                            trackZone: "filtres_commun",
                            trackLabel: "filtre_prix",
                            trackAction: "filtre_prix"
                        }
                    }
                )
            }

            if (this.compatibilityCheckboxes.length > 0) {
                cards.push(
                    {
                        headingId: "filter-compatibility-header",
                        collapseId: "filter-compatibility-block",
                        icon: null,
                        title: "Compatibilité",
                        expanded: true,
                        tms : {
                            trackZone: "filtres_commun",
                            trackLabel: "filtre_compatibility",
                            trackAction: "filtre_compatibility"
                        }
                    }
                )
            }

            if (this.noteCheckboxes.length > 0) {
                cards.push(
                    {
                        headingId: "filter-note-header",
                        collapseId: "filter-note-block",
                        icon: null,
                        title: "Avis client",
                        expanded: true,
                        tms : {
                            trackZone: "filtres_commun",
                            trackLabel: "filtre_avis",
                            trackAction: "filtre_avis"
                        }
                    }
                )
            }

            if (this.efficiencyClassesCheckboxes.length > 0) {
                cards.push(
                    {
                        headingId: "filter-efficiency-classes-header",
                        collapseId: "filter-efficiency-classes-block",
                        icon: null,
                        title: "Label Energie",
                        expanded: true,
                        tms : {
                            trackZone: "filtres_commun",
                            trackLabel: "filtre_efficiency_classes",
                            trackAction: "filtre_efficiency_classes"
                        }
                    }
                )
            }
            return cards;
        },
        collapsableBrandButtonLabel() {
            if (this.brandsCollapse) {
                return this.collapsedBrandsLabel + '<span aria-hidden=true>(' + this.collapsableBrandCheckboxes.length + ')</span>';
            } else {
                return this.notCollapsedBrandsLabel;
            }
        },
        collapsableEfficiencyClassesButtonLabel() {
            if (this.efficiencyClassesCollapse) {
                return 'Voir plus '+ '<span aria-hidden=true>(' + this.collapsableEfficiencyClassesCheckboxes.length + ')</span>';
            } else {
                return 'Voir moins';
            }
        },
        availableBrandCheckboxes() {
            let filteredBrands = this.brandCheckboxes.filter(checkbox => !checkbox.disabled);

            // Filtrer les marques dès qu'un caractère est saisi
            if (this.brandSearchValue.length > 0) {
                const searchTerm = this.brandSearchValue.toLowerCase();
                filteredBrands = filteredBrands.filter(checkbox =>
                    checkbox.label.toLowerCase().includes(searchTerm)
                );
            }

            return filteredBrands;
        },
        notCollapsableBrandCheckboxes() {
            let filtersBrandSize = this.availableBrandCheckboxes.length - this.notCollapsableBrandNumber;
            if (filtersBrandSize > 0)
                return this.availableBrandCheckboxes.slice(0, this.notCollapsableBrandNumber);
            else return this.availableBrandCheckboxes;
        },
        collapsableBrandCheckboxes() {
            let filtersBrandSize = this.availableBrandCheckboxes.length - this.notCollapsableBrandNumber;
            if (filtersBrandSize > 0)
                return this.availableBrandCheckboxes.slice(this.notCollapsableBrandNumber, this.availableBrandCheckboxes.length);
            else return [];
        },
        notCollapsableEfficiencyClassesCheckboxes() {
            let filtersEfficiencyClassesSize = this.efficiencyClassesCheckboxes.length - 3;
            if (filtersEfficiencyClassesSize > 0)
                return this.efficiencyClassesCheckboxes.slice(0, 3);
            else return this.efficiencyClassesCheckboxes;
        },
        collapsableEfficiencyClassesCheckboxes() {
            let filtersEfficiencyClassesSize = this.efficiencyClassesCheckboxes.length - 3;
            if (filtersEfficiencyClassesSize > 0)
                return this.efficiencyClassesCheckboxes.slice(3,  this.efficiencyClassesCheckboxes.length);
            else return [];
        },
        availableBrandFoundLabel() {
            if (this.brandSearchValue.length > 0) {
                const brandCount = this.availableBrandCheckboxes.length;
                if (brandCount <= 1) return brandCount + " marque trouvée ";
                else return this.availableBrandCheckboxes.length + " marques trouvées";
            } else return "";
        },
        hasNoSubCategories() {
            return this.categories.every(category => category.subCategories.length === 0);
        },
        shopBrandLabel() {
            return this.shopBrand === 'ORANGE' ? 'Orange' : 'Sosh'
        }
    },
    watch: {
        availableBrandCheckboxes(newValue) {
            // Réinitialise le booléen quand le nombre de marques filtrées est inférieur ou égal au seuil.
            // En effet, si le nombre de marques filtrées redevient supérieur au seuil, le bloc sera automatiquement collapsé.
            if (newValue.length <= this.notCollapsableBrandNumber) {
                this.brandsCollapse = true;
            }
        }
    },
    mounted() {
        this.brandHide();
        this.efficiencyClassesHide();

    },
    methods: {
        brandShow() {
            this.brandsCollapse = false;
            if (this.collapsableBrandCheckboxes.length > 0) {
                // Au clic sur "Voir plus", le focus est mis sur la première case à cocher qu'on vient de faire apparaître
                let firstBrandId = this.collapsableBrandCheckboxes[0].id;
                document.getElementById(firstBrandId).focus();
            }
        },
        brandHide() {
            this.brandsCollapse = true;
        },
        efficiencyClassesShow() {
            this.efficiencyClassesCollapse = false;
            if (this.collapsableEfficiencyClassesCheckboxes.length > 0) {
                // Au clic sur "Voir plus", le focus est mis sur la première case à cocher qu'on vient de faire apparaître
                let firstEfficiencyClassId = this.collapsableEfficiencyClassesCheckboxes[0].id;
                document.getElementById(firstEfficiencyClassId).focus();
            }
        },
        efficiencyClassesHide() {
            this.efficiencyClassesCollapse = true;
        },
        filterBrands: function (value) {
            this.$emit('update-brand-search-value', value);
        },
        ratingText: function (checkbox) {
            let text = checkbox.count != null ? ' (' + checkbox.count + ')' : '';
            if (checkbox.value === "5") {
                return text;
            } else {
                return 'et plus ' + text;
            }
        },
        toggleFirstPartyOnly: function () {
            this.$emit('toggle-first-party-only-filter');
        },
        selectSearchCategory: function (categoryValue) {
            this.$emit('select-search-category-filter', categoryValue);
        },
        toggleBrand: function (brandId, isChecked) {
            this.$emit('toggle-brand-filter', brandId, isChecked);
        },
        togglePrice: function (priceId, isChecked) {
            this.$emit('toggle-price-filter', priceId, isChecked);
        },
        toggleNote: function (noteId, isChecked) {
            this.$emit('toggle-note-filter', noteId, isChecked);
        },
        toggleSelector: function (selectorId, isChecked) {
            this.$emit('toggle-selector-filter', selectorId, isChecked);
        },
        toggleStatus: function (statusId, isChecked) {
            this.$emit('toggle-status-filter', statusId, isChecked);
        },
        toggleCompatibility: function (compatibilityId, isChecked) {
            this.$emit('toggle-compatibility-filter', compatibilityId, isChecked);
        },
        toggleBonusRanges: function (bonusId, isChecked) {
            this.$emit('toggle-bonus-filter', bonusId, isChecked);
        },
        toggleEfficiencyClass: function (efficiencyClassId, isChecked) {
            this.$emit('toggle-efficiency-class-filter', efficiencyClassId, isChecked);
        },
        selectCategory: function (categoryId, subCategoryId) {
            this.$emit('select-category-filter', categoryId, subCategoryId);
        },
        trackTms: function (card, isExpanded) {
            if (!card || !card.tms) return;
            const type = isExpanded ? "ouverture" : "fermeture";
            const actionLabel = `${type}_${card.tms.trackAction}`;
            Tms.link({
                category: card.tms.trackZone,
                action: card.tms.trackLabel,
                label: actionLabel
            });
        },
        tmsCategory(categoryLabel) {
            return `header_${toTmsFormat(categoryLabel)}`;
        },
        tmsAction(actionLabel) {
            return `categorie_${toTmsFormat(actionLabel)}`;
        },
        tmsLabel(label) {
            return toTmsFormat(label);
        }
    }
};
</script>

<style lang="scss">
@import 'ob1_common';

#device-list-filters li {
    padding-left: px-to-rem(1); // Pour que les cases à cocher soient masquées lorsqu'elles sont sous le bouton sticky
}

.filter-rating-item {
    margin-right: px-to-rem(5);
}

#device-list-filters-categories .card {
    .card-header button {
        border-top-color: #ccc;
    }

    &:first-child .card-header button {
        border-top-color: transparent;
    }

    .card-header:has([aria-expanded=true]) button,
    &:has(.card-header [aria-expanded=true]) + .card .card-header button {
        border-top-color: black;
    }

    .accordion-card-title {
        min-height: px-to-rem(60);
    }
}

#device-list-filters .ob1-accordion-large {
    min-height: px-to-rem(60);
    padding: 10px 0;
}

</style>
